import StaticReportsLayout from '@/Layouts/StaticReportsLayout'
import AddSecurities from '@/components/WhatIfAnalysis/AddSecurities'
import PortfolioTable from '@/components/WhatIfAnalysis/PortfolioTable'
import SecurityAccordion from '@/components/WhatIfAnalysis/SecurityAccordion'
import SecurityListsPanel from '@/components/WhatIfAnalysis/SecurityListsPanel'
import { ModuleTypePageTitle } from '@/enums/axiom-report.enum'
import {
  calcmkval,
  clearWhatIfPortfolioCache,
  DEFAULT_WHAT_IF_PORTFOLIO_RANGE,
  getSecurityDetails,
  getTargetDurContResult,
  getTargetMkvalResult,
  getTargetPmkvalResult,
  getTargetTradesResult,
  getWhatIfPortfolios,
  getWhatIfSectorComposition,
  getWhatIfSummary,
  saveWhatIfPortfolio,
  SaveWhatIfPortfolioRequest,
  transformSummaryData,
  WhatIfHolding,
  WhatIfPortfolioRangeKey
} from '@/services/whatIfAnalysis.service'
import { setSelectedPortfolios } from '@/states/portfolioOptmization.slice'
import {
  validateNumber,
  validatePercentage
} from '@/utils/UserInputValidation'
import axiosInstance from '@/utils/interceptor'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

interface WhatIfReportsPageProps {
  title?: string
}

interface SelectedPortfolio {
  id: string
  name: string
  date: string
  currency: string
  benchmarkDescription: string
  numOfSecurities: number
  marketValue?: number
  uniqueId?: string
}

interface SecurityData {
  secid: string
  axsid?: string | number
  description?: string
  coupon?: number | string
  currency?: string
  country?: string
  price: number | string
  edur: number | null
  qual: string
  holdings_current: number
  holdings_whatif: number
  mkval: number | string
  pmkval_current: number
  pmkval_whatif: number
  sector: string
  dur_contr_current: number
  dur_contr_whatif: number
  mkval_current?: number
  mval?: number
  fmval?: number
  currencyCode: number
  baseCurrencyCode: number
  gainloss?: number
}

interface cashsec {
  axsid?: number
  secid: string
  pmkval: number
  dur_contr: number
  asset: number
  description: string
  price: number
  yield: number
  coup: number
  matu: number
  edur: number
  pdur: number
  mkval: number
  country: string
  currency: string
  currency_code: number
  sector: string
  qual: string
  mval: number
  fmval: number
  gainloss: number
}

const portfolioKey = (p: SelectedPortfolio | null) =>
  p ? `${p.name}:${dayjs(p.date).format('YYYYMMDD')}` : null

const WhatIfReportsPage: React.FC<WhatIfReportsPageProps> = ({ title }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const restoreKeyRef = React.useRef<string | null>(null)
  
  // Initialize adjustCashToggle from localStorage
  const initializeAdjustCashToggle = () => {
    const saved = localStorage.getItem('whatif-adjustCashToggle')
    return saved !== null ? JSON.parse(saved) : true
  }
  
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<SelectedPortfolio | null>(null)
  const [portfolios, setPortfolios] = useState<any[]>([])
  const [portfoliosLoading, setPortfoliosLoading] = useState<boolean>(false)
  const [portfoliosError, setPortfoliosError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [securities, setSecurities] = useState<SecurityData[]>([])
  const [securitiesLoading, setSecuritiesLoading] = useState<boolean>(false)
  const [securitiesError, setSecuritiesError] = useState<string | null>(null)
  const [availableSecurities, setAvailableSecurities] = useState<any[]>([])
  const [addSecuritiesLoading, setAddSecuritiesLoading] =
    useState<boolean>(false)
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null)
  const [runningAnalysis, setRunningAnalysis] = useState<boolean>(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [portfolioMkval, setPortfolioMkval] = useState<number | null>(null)
  const [cumulativeCashMkval, setCumulativeCashMkval] = useState<number>(0)
  const [tradeCalculationsLoading, setTradeCalculationsLoading] =
    useState<boolean>(false)
  const pendingTradeRequestsRef = React.useRef(0)
  const [deleteWarning, setDeleteWarning] = useState<string | null>(null)
  const refreshCalledRef = React.useRef(false)
  const [adjustCashToggle, setAdjustCashToggle] = useState<boolean>(initializeAdjustCashToggle())
  const [portfolioRange, setPortfolioRange] = useState<WhatIfPortfolioRangeKey>(
    DEFAULT_WHAT_IF_PORTFOLIO_RANGE,
  )
  const latestPortfolioRangeRequestRef = React.useRef<WhatIfPortfolioRangeKey | null>(null)

  // Persist adjustCashToggle state to localStorage
  useEffect(() => {
    localStorage.setItem('whatif-adjustCashToggle', JSON.stringify(adjustCashToggle))
  }, [adjustCashToggle])

  // Function to handle cash adjustment (UI-level calculations, no cash security)
  const getCashAdjustment = async (updatedSecurities: SecurityData[], targetMkval: number, originalSecurityMkval: number, editedSecuritySecid?: string): Promise<SecurityData[]> => {
    if (!selectedPortfolio) return updatedSecurities

    try {
      // Remove any cash security if it exists
      const securitiesWithoutCash = updatedSecurities.filter(sec => sec.secid !== 'USDCASH1')
      
      // Call SAVE and GET to update duration contributions
      const finalSecurities = await saveAndGetUpdatedDurations(securitiesWithoutCash)
      return finalSecurities
      
    } catch (error) {
      console.warn('Failed to handle cash adjustment (old method):', error)
      return updatedSecurities.filter(sec => sec.secid !== 'USDCASH1')
    }
  }

  // Unified cash adjustment function that chooses method based on toggle state
  const handleCashAdjustment = async (updatedSecurities: SecurityData[], targetMkval: number, originalSecurityMkval: number, editedSecuritySecid?: string): Promise<SecurityData[]> => {
    if (adjustCashToggle) {
      // 1 - Use cash security
      return await getCashSec(updatedSecurities, targetMkval, originalSecurityMkval, editedSecuritySecid)
    } else {
      // 2 - UI-level calculations, no cash security
      return await getCashAdjustment(updatedSecurities, targetMkval, originalSecurityMkval, editedSecuritySecid)
    }
  }

  // Function to handle cash security adjustment after user edits
  const getCashSec = async (updatedSecurities: SecurityData[], targetMkval: number, originalSecurityMkval: number, editedSecuritySecid?: string): Promise<SecurityData[]> => {
    if (!selectedPortfolio) return updatedSecurities

    try {
      const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
      
      // Check if user is editing cash security what-if values, if so, skip cash processing
      if (editedSecuritySecid === 'USDCASH1') {
        await savePortfolio(updatedSecurities)
        
        // After saving, get updated %port values for all securities
        try {
          const response = await axiosInstance.get(
            `whatif/api/v1/whatif/get?date=${formattedDate}&portfolioName=${selectedPortfolio.name}`,
          )
          
          const rawData = response.data || {}
          const whatIfSecurityList = (rawData.whatIfSec || [])
          
          // Update all securities with new %port values from the response
          const securitiesWithUpdatedPmkval = updatedSecurities.map(sec => {
            const updatedSec = whatIfSecurityList.find((whatIfSec: any) => whatIfSec.secid === sec.secid)
            if (updatedSec) {
              return {
                ...sec,
                pmkval_whatif: updatedSec.pmkval || 0
              }
            }
            return sec
          })
          
          return securitiesWithUpdatedPmkval
        } catch (error) {
          console.warn('Failed to fetch updated %port values after cash security edit:', error)
          return updatedSecurities
        }
    }
      
      // Fetch cash security details using getSecurityDetails
      let usdcash1Details
      try {
        usdcash1Details = await getSecurityDetails('USDCASH1', formattedDate)
      } catch (error) {
        console.warn('Failed to fetch cash security details:', error)
        return updatedSecurities
      }

      if (!usdcash1Details) {
        console.warn('No cash security details available')
        return updatedSecurities
      }

      // Check if all what-if trades are zero (no what-if scenario)
      const allWhatIfTradesZero = updatedSecurities
        .filter(sec => sec.secid !== 'USDCASH1')
        .every(sec => (sec.holdings_whatif || 0) === 0)

      // If all what-if trades are zero, delete cash security and reset cumulative
      if (allWhatIfTradesZero) {
        setCumulativeCashMkval(0)
        const securitiesWithoutCash = updatedSecurities.filter(sec => sec.secid !== 'USDCASH1')
        
        // Save portfolio with cash security removed
        await savePortfolio(securitiesWithoutCash)
        
        return securitiesWithoutCash
      }

      // Calculate total what-if mkval from all updated securities (excluding cash)
      const totalWhatIfMkval = updatedSecurities
        .filter(sec => sec.secid !== 'USDCASH1')
        .reduce((total, sec) => {
          const mkval = typeof sec.mkval === 'string' ? parseFloat(sec.mkval) : Number(sec.mkval)
          return total + (mkval || 0)
        }, 0)

      // Cash mkval = original portfolio mkval - total what-if mkval
      const cashMkval = (portfolioMkval || 0) - totalWhatIfMkval
      setCumulativeCashMkval(cashMkval)

      const cashParams = {
        mkval: cashMkval, 
        date: formattedDate,
        units: 0,
        mval: usdcash1Details.mval || 0,
        fmval: usdcash1Details.fmval || usdcash1Details.mval || 0,
        durVal: usdcash1Details.edur || 0,
        currencyCode: usdcash1Details.currency_code || 840,
        baseCurrencyCode: usdcash1Details.currency_code || 840,
        portMkval: portfolioMkval || 0
      }

      const cashResult = await getTargetMkvalResult(cashParams)
      
      // Create/update cash security
      const cashSecurity: SecurityData = {
        secid: 'USDCASH1',
        axsid: usdcash1Details.axsid || 0,
        price: usdcash1Details.price ? usdcash1Details.price.toFixed(2) : '0.00',
        edur: usdcash1Details.edur || null,
        qual: usdcash1Details.qual || 'N/A',
        holdings_current: Number(usdcash1Details.holdings ?? 0),
        holdings_whatif: cashResult.trades,
        mkval: cashResult.mkval,
        mkval_current: Number(usdcash1Details.mkval ?? 0),
        pmkval_current: Number(usdcash1Details.pmkval ?? 0),
        pmkval_whatif: cashResult.pmkval,
        sector: usdcash1Details.sector || 'Cash',
        dur_contr_current: Number(usdcash1Details.dur_contr ?? 0),
        dur_contr_whatif: cashResult.durContrib,
        description: usdcash1Details.description || 'US Dollar Cash',
        country: usdcash1Details.country || 'US',
        currency: usdcash1Details.currency || 'USD',
        mval: Number(usdcash1Details.mval ?? 0),
        fmval: Number(usdcash1Details.fmval ?? usdcash1Details.mval ?? 0),
        currencyCode: usdcash1Details.currency_code || 840,
        baseCurrencyCode: usdcash1Details.currency_code || 840,
        gainloss: Number(usdcash1Details.gainloss ?? 0),
      }

      // Update securities array - add or update cash security
      const finalSecurities = updatedSecurities.map(sec => 
        sec.secid === 'USDCASH1' ? cashSecurity : sec
      )

      // If cash security doesn't exist, add it at the top
      if (!updatedSecurities.some(sec => sec.secid === 'USDCASH1')) {
        finalSecurities.unshift(cashSecurity)
      } else {
        finalSecurities.sort((a, b) => {
          if (a.secid === 'USDCASH1') return -1
          if (b.secid === 'USDCASH1') return 1

          const isANewSecurity = (a.holdings_current === 0 && a.mkval_current === 0)
          const isBNewSecurity = (b.holdings_current === 0 && b.mkval_current === 0)

          if (isANewSecurity && !isBNewSecurity) return -1
          if (!isANewSecurity && isBNewSecurity) return 1

          return 0
        })
      }

      // Save portfolio with cash security included
      await savePortfolio(finalSecurities)
      
      return finalSecurities
    } catch (error) {
      console.warn('Failed to handle cash security:', error)
      return updatedSecurities
    }
  }

  // Helper function to save portfolio
  const savePortfolio = async (securities: SecurityData[]) => {
    if (!selectedPortfolio) return

    const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
    
    const holdings: WhatIfHolding[] = securities.map((sec: any) => ({
      axsid: Number(sec.axsid) || 0, 
      hld: (sec.holdings_current || 0) + (sec.holdings_whatif || 0)
    }))

    const saveRequest: SaveWhatIfPortfolioRequest = {
      replaceIfExists: true,
      portDate: Number(formattedDate),
      portName: selectedPortfolio.name,
      whatIfPortName: '', 
      holdings
    }

    await saveWhatIfPortfolio(saveRequest)
  }

  // Helper function to save portfolio and get updated duration contributions
  const saveAndGetUpdatedDurations = async (securities: SecurityData[]): Promise<SecurityData[]> => {
    if (!selectedPortfolio) return securities

    const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
    
    // First save the portfolio
    await savePortfolio(securities)
    
    // Then fetch updated data to get server-calculated %port and dur_contr values
    try {
      const response = await axiosInstance.get(
        `whatif/api/v1/whatif/get?date=${formattedDate}&portfolioName=${selectedPortfolio.name}`,
      )
      
      const rawData = response.data || {}
      const whatIfSecurityList = (rawData.whatIfSec || [])
      
      // Update securities with new server-calculated values
      const updatedSecurities = securities.map(sec => {
        const updatedSec = whatIfSecurityList.find((whatIfSec: any) => whatIfSec.secid === sec.secid)
        if (updatedSec) {
          return {
            ...sec,
            pmkval_whatif: updatedSec.pmkval || 0,
            dur_contr_whatif: updatedSec.dur_contr || 0,
            mkval: updatedSec.mkval || sec.mkval
          }
        }
        return sec
      })
      
      return updatedSecurities
    } catch (error) {
      console.warn('Failed to fetch updated duration contributions:', error)
      return securities
    }
  }

  // Fetch the What-If portfolio list for the given date range
  const loadPortfolios = async (range: WhatIfPortfolioRangeKey) => {
    latestPortfolioRangeRequestRef.current = range
    setPortfoliosLoading(true)
    setPortfoliosError(null)

    try {
      let portfolioData: any[]
      try {
        portfolioData = await getWhatIfPortfolios(range)
      } catch (err) {
        if (range === DEFAULT_WHAT_IF_PORTFOLIO_RANGE) throw err
        console.warn(`Failed to fetch portfolios for range "${range}", falling back to the default range:`, err)
        if (latestPortfolioRangeRequestRef.current !== range) return
        setPortfolioRange(DEFAULT_WHAT_IF_PORTFOLIO_RANGE)
        setNotificationMessage('Unable to load portfolios for that range. Showing the default range instead.')
        setTimeout(() => setNotificationMessage(null), 4000)
        portfolioData = await getWhatIfPortfolios(DEFAULT_WHAT_IF_PORTFOLIO_RANGE)
      }

      if (latestPortfolioRangeRequestRef.current !== range) return

      // Ensure each portfolio has an id field
      const portfoliosWithId = portfolioData.map(
        (portfolio: any, index: number) => ({
          ...portfolio,
          id: portfolio.id || portfolio.name || `portfolio-${index}`,
          currency: portfolio.currency,
          benchmarkDescription: portfolio.benchmarkDescription,
          numOfSecurities: portfolio.numOfSecurities,
          marketValue: portfolio.mkval,
        }),
      )
      setPortfolios(portfoliosWithId)
    } catch (err) {
      if (latestPortfolioRangeRequestRef.current !== range) return
      setPortfolios([])
      setPortfoliosError(
        err instanceof Error ? err.message : 'Failed to fetch portfolios',
      )
    } finally {
      if (latestPortfolioRangeRequestRef.current === range) {
        setPortfoliosLoading(false)
      }
    }
  }

  // Switch the portfolio list to a different date range
  const handlePortfolioRangeChange = (range: WhatIfPortfolioRangeKey) => {
    setPortfolioRange(range)
    loadPortfolios(range)
  }

  // Fetch portfolios on component mount
  useEffect(() => {
    const navState = location.state as any

    // If we're in a restore scenario
    if (navState?.restore) {
      // If portfolio was not saved, use portfolios from navigation state
      if (!navState?.refreshPortfolios) {
        setPortfolios(navState.portfolios || [])
      }
      // If portfolio was saved, skip here (will be handled by restore useEffect)
      return
    }

    // Normal initial load (no navigation state)
    loadPortfolios(portfolioRange)
  }, [location.state])

  // Auto-clear delete warning after 10 seconds
  useEffect(() => {
    if (!deleteWarning) return

    const timer = setTimeout(() => {
      setDeleteWarning(null)
    }, 5000)

    return () => {
      clearTimeout(timer)
    }
  }, [deleteWarning])

  // Restore state from navigation (dashboard back button)
  useEffect(() => {
    const navState = location.state as any
    if (!navState?.restore) {
      return
    }

    const restoredPortfolio =
      navState.selectedPortfolio as SelectedPortfolio | null
    const restoredSecurities = navState.securities as SecurityData[] | undefined
    const restoredAdjustCashToggle = navState.adjustCashToggle as boolean | undefined
    const restoredPortfolioRange = navState.portfolioRange as WhatIfPortfolioRangeKey | undefined

    const key = portfolioKey(restoredPortfolio)
    restoreKeyRef.current = key

    if (restoredPortfolio) {
      setSelectedPortfolio(restoredPortfolio)
      // Restore original portfolio market value so what-if APIs receive correct portMkval
      const mv = (restoredPortfolio as any).marketValue
      const mvNum = mv !== undefined && mv !== null ? Number(mv) : NaN
      if (!Number.isNaN(mvNum)) {
        setPortfolioMkval(mvNum)
      }
    }
    if (restoredSecurities) setSecurities(restoredSecurities)

    // Restore cash adjustment toggle state
    if (restoredAdjustCashToggle !== undefined) {
      setAdjustCashToggle(restoredAdjustCashToggle)
    }

    // Restore portfolio date-range selection
    if (restoredPortfolioRange !== undefined) {
      setPortfolioRange(restoredPortfolioRange)
    }

    // Refresh portfolios list when returning from dashboard to get any newly saved portfolios
    const refreshPortfolios = async () => {
      // Prevent duplicate calls
      if (refreshCalledRef.current) {
        return
      }
      refreshCalledRef.current = true

      clearWhatIfPortfolioCache() // Clear cache to force fresh API call

      await loadPortfolios(restoredPortfolioRange ?? portfolioRange)

      // Reset ref after a delay to allow future refreshes
      setTimeout(() => {
        refreshCalledRef.current = false
      }, 1000)
    }

    // Only refresh portfolios when portfolio was saved
    if (navState?.refreshPortfolios) {
      refreshPortfolios()
    }
  }, [location.state])

  // Sync restored selectedPortfolio with loaded portfolios to get uniqueId for grid selection
  useEffect(() => {
    if (!selectedPortfolio || portfolios.length === 0) return
    // If selectedPortfolio already has uniqueId, no need to sync
    if (selectedPortfolio.uniqueId) return

    // Find matching portfolio in the loaded list by name and date
    const matchIndex = portfolios.findIndex(
      (p) =>
        p.name === selectedPortfolio.name &&
        dayjs(p.date).format('YYYYMMDD') ===
          dayjs(selectedPortfolio.date).format('YYYYMMDD'),
    )

    if (matchIndex !== -1) {
      const matched = portfolios[matchIndex]
      // Update selectedPortfolio with uniqueId so grid highlights the row
      setSelectedPortfolio({
        ...selectedPortfolio,
        uniqueId: `${matched.id || matched.name}-${matchIndex}`,
      })
    }
  }, [portfolios, selectedPortfolio])

  // Fetch securities when portfolio is selected
  useEffect(() => {
    if (!selectedPortfolio) {
      // Only clear securities if we're not in a restore scenario
      if (!restoreKeyRef.current) {
        setSecurities([])
      }
      setSecuritiesError(null)
      return
    }

    const currentKey = portfolioKey(selectedPortfolio)
    if (restoreKeyRef.current && restoreKeyRef.current === currentKey) {
      restoreKeyRef.current = null
      return
    }

    const fetchSecurities = async () => {
      setSecuritiesLoading(true)
      setSecuritiesError(null)
      try {
        const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
        
        const response = await axiosInstance.get(
          `whatif/api/v1/whatif/get?date=${formattedDate}&portfolioName=${selectedPortfolio.name}`,
        )

        const rawData = response.data || {}
        const securityList = (rawData.sec || [])
        
        // Extract what-if securities for what-if panel
        const whatIfSecurityList = (rawData.whatIfSec || [])
        
        const originalSecurityMap = new Map()
        securityList.forEach((sec: any) => {
          originalSecurityMap.set(sec.secid, sec)
        })
        
        // Merge both sec[] and whatIfSec[] arrays, eliminating duplicates
        const mergedSecuritiesMap = new Map()
        
        // Add original securities first
        securityList.forEach((sec: any) => {
          mergedSecuritiesMap.set(sec.secid, {
            ...sec,
            source: 'original',
            holdings_current: sec.holdings || 0,
            holdings_whatif: 0, // Original securities start with 0 what-if
            mkval_current: sec.mkval || 0,
            pmkval_current: sec.pmkval || 0,
            dur_contr_current: sec.dur_contr || 0,
          })
        })
        
        // Add/merge what-if securities
        whatIfSecurityList.forEach((whatIfSec: any) => {
          const existing = mergedSecuritiesMap.get(whatIfSec.secid)
          if (existing) {
            // Update existing security with what-if data
            mergedSecuritiesMap.set(whatIfSec.secid, {
              ...existing,
              ...whatIfSec,
              source: 'both',
              holdings_whatif: whatIfSec.holdings - (existing.holdings_current || 0),
              mkval: whatIfSec.mkval,
              pmkval_whatif: whatIfSec.pmkval,
              dur_contr_whatif: whatIfSec.dur_contr,
            })
          } else {
            // Add new what-if security
            mergedSecuritiesMap.set(whatIfSec.secid, {
              ...whatIfSec,
              source: 'whatif',
              holdings_current: 0,
              holdings_whatif: whatIfSec.holdings,
              mkval_current: 0,
              pmkval_current: 0,
              pmkval_whatif: whatIfSec.pmkval,
              dur_contr_current: 0,
              dur_contr_whatif: whatIfSec.dur_contr
            })
          }
        })
        
        // Handle original securities not in what-if list (deleted securities)
        const whatIfSecurityIds = new Set(whatIfSecurityList.map((sec: any) => sec.secid))
        
        // Update original securities that are not in what-if list
        mergedSecuritiesMap.forEach((sec: any, secid: string) => {
          if (sec.source === 'original' && !whatIfSecurityIds.has(secid)) {
            // Security was deleted from what-if portfolio
            mergedSecuritiesMap.set(secid, {
              ...sec,
              holdings_whatif: -(sec.holdings_current || 0),
              mkval: 0,
              pmkval_whatif: 0,
              dur_contr_whatif: 0,
            })
          }
        })
        
        const baseCurrencyCode = rawData.port?.base_currency_code
        const portMkval = rawData.port?.mkval
        if (typeof portMkval === 'number') {
          setPortfolioMkval(portMkval)
        }
        
        // Convert map to array and format for display
        let formattedSecurities = Array.from(mergedSecuritiesMap.values()).map((sec: any) => {
          return {
            ...sec,
            axsid: sec.axsid || 0, 
            baseCurrencyCode: baseCurrencyCode,
            currencyCode: sec.currency_code || sec.currencyCode,
            gainloss: sec.gainloss || 0,
          }
        })

        // Sort to ensure cash security (USDCASH1) is always at the top, followed by newly added securities
        formattedSecurities.sort((a, b) => {
          // Prioritize USDCASH1
          if (a.secid === 'USDCASH1') return -1
          if (b.secid === 'USDCASH1') return 1

          // Then prioritize newly added securities (holdings_current or mkval_current is 0)
          const isANewSecurity = (a.holdings_current === 0 && a.mkval_current === 0)
          const isBNewSecurity = (b.holdings_current === 0 && b.mkval_current === 0)

          if (isANewSecurity && !isBNewSecurity) return -1
          if (!isANewSecurity && isBNewSecurity) return 1

          return 0
        })

        setSecurities(formattedSecurities)
      } catch (err) {
        setSecuritiesError(
          err instanceof Error ? err.message : 'Failed to fetch securities',
        )
        setSecurities([])
      } finally {
        setSecuritiesLoading(false)
      }
    }

    fetchSecurities()
  }, [selectedPortfolio])

  // Set up portfolio context for screening modal
  useEffect(() => {
    if (selectedPortfolio) {
      const currentDate =
        selectedPortfolio.date ||
        new Date().toISOString().slice(0, 10).replace(/-/g, '')
      let portfolioDate = currentDate

      // Convert MM/DD/YYYY to YYYYMMDD format if needed
      if (portfolioDate.includes('/')) {
        const [month, day, year] = portfolioDate.split('/')
        portfolioDate = `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`
      }

      const portfolioForScreening = {
        name: selectedPortfolio.name,
        description: selectedPortfolio.name
          ? `Portfolio: ${selectedPortfolio.name}`
          : '',
        benchmarkName: selectedPortfolio.benchmarkDescription,
        benchmarkDescription: selectedPortfolio.benchmarkDescription,
        dates: [portfolioDate],
        date: portfolioDate,
        type: 'OSEVP',
        numOfSecurities:
          selectedPortfolio.numOfSecurities || securities?.length || 0,
        currency: selectedPortfolio.currency,
        user: undefined,
        constraints: undefined,
        marketValue: selectedPortfolio.marketValue?.toString(),
      }

      dispatch(setSelectedPortfolios([portfolioForScreening]))
    }
  }, [selectedPortfolio, securities, dispatch])

  const recalculateTotalMarketValue = (items: SecurityData[]) => {
    const total = items.reduce((sum, sec) => {
      const mkvalNum =
        sec.mkval !== undefined && sec.mkval !== null ? Number(sec.mkval) : 0
      if (Number.isNaN(mkvalNum)) {
        return sum
      }
      return sum + mkvalNum
    }, 0)

    setPortfolioMkval(total)
    return total
  }

  const handleAddSecurity = (newSecurity: SecurityData) => {
    // Add the new security at the top of the list (after pinned rows)
    setSecurities((prevSecurities) => [newSecurity, ...prevSecurities])
  }

  const startTradeCalculation = () => {
    pendingTradeRequestsRef.current += 1
    setTradeCalculationsLoading(true)
  }

  const endTradeCalculation = () => {
    pendingTradeRequestsRef.current -= 1
    if (pendingTradeRequestsRef.current <= 0) {
      pendingTradeRequestsRef.current = 0
      setTradeCalculationsLoading(false)
    }
  }

  // Check if any what-if modifications have been made
  const hasWhatIfChanges = () => {
    return securities.some(
      (security) =>
        security.holdings_whatif !== 0 ||
        security.pmkval_whatif !== 0 ||
        security.dur_contr_whatif !== 0,
    )
  }

  const handleUpdateSecurity = (
    index: number,
    updatedSecurity: SecurityData,
  ) => {
    const prevRow = securities[index]
    if (!prevRow) return

    // Helper function to validate market value and handle errors
    const validateAndHandleMarketValue = (marketValue: any, nextSecurities: any[]): boolean => {
      const marketValueValidation = validateNumber(marketValue)
      if (!marketValueValidation.isValid) {
        setNotificationMessage(marketValueValidation.error || 'Invalid market value calculated. Please try again.')
        // Revert to previous values
        setSecurities((prev) =>
          prev.map((sec) =>
            sec.secid === updatedSecurity.secid
              ? prevRow 
              : sec,
          ),
        )
        return false
      }
      return true
    }

    // Determine which What-If field changed
    const quantityChanged =
      prevRow.holdings_whatif !== updatedSecurity.holdings_whatif
    const portChanged =
      prevRow.pmkval_whatif !== updatedSecurity.pmkval_whatif
    const durChanged =
      prevRow.dur_contr_whatif !== updatedSecurity.dur_contr_whatif

    setSecurities((prevSecurities) =>
      prevSecurities.map((security, i) =>
        i === index ? updatedSecurity : security,
      ),
    )

    if (!selectedPortfolio) {
      return
    }

    const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
    const rawFallbackPortMkval = (selectedPortfolio as any).marketValue
    const fallbackPortMkvalNum =
      rawFallbackPortMkval !== undefined && rawFallbackPortMkval !== null
        ? Number(rawFallbackPortMkval)
        : NaN

    const effectivePortMkval =
      portfolioMkval && portfolioMkval !== 0
        ? portfolioMkval
        : !Number.isNaN(fallbackPortMkvalNum)
        ? fallbackPortMkvalNum
        : 0

    // %PORT What-If edit -> Target %PORT API only
    if (portChanged) {
      // API Validation - Validate market value parameters
      const formattedDate = dayjs(selectedPortfolio?.date).format('YYYYMMDD')
      if (!formattedDate || isNaN(Number(formattedDate))) {
        setNotificationMessage('Invalid portfolio date. Please select a valid portfolio.')
        return
      }
      const targetPmkval = (updatedSecurity as any).pmkval_whatif ?? 0
      const percentageValidation = validatePercentage(targetPmkval)
      if (!percentageValidation.isValid) {
        setNotificationMessage(percentageValidation.error || 'Invalid target percentage')
        return
      }
      const newPercentage = Number((updatedSecurity as any).pmkval_whatif ?? 0)
      
      // Calculate portfolio market value for target API call
      // When not using cash security, use current recalculated mkval
      const currentPortfolioMkval = adjustCashToggle 
        ? (effectivePortMkval ?? 0)
        : recalculateTotalMarketValue(securities)

      const pmParams = {
        pmkval: Number((updatedSecurity as any).pmkval_whatif ?? 0),
        date: formattedDate,
        units: updatedSecurity.holdings_current || 0,
        mval: Number(updatedSecurity.mval ?? 0),
        fmval: Number(updatedSecurity.fmval ?? updatedSecurity.mval ?? 0),
        durVal: Number(updatedSecurity.edur ?? 0),
        currencyCode: updatedSecurity.currencyCode ?? 0,
        baseCurrencyCode: updatedSecurity.baseCurrencyCode ?? 0,
        portMkval: Number(currentPortfolioMkval),
      }

      startTradeCalculation()

      getTargetPmkvalResult(pmParams)
        .then(async (pmResult) => {
          const nextSecurities = securities.map((sec) =>
            sec.secid === updatedSecurity.secid
              ? {
                  ...sec,
                  holdings_whatif: pmResult.trades,
                  mkval: pmResult.mkval,
                  pmkval_whatif: pmResult.pmkval,
                  dur_contr_whatif: pmResult.durContrib,
                }
              : sec,
          )

          // Validate market value is a valid number
          if (!validateAndHandleMarketValue(pmResult.mkval, nextSecurities)) {
            return
          }

          // Handle cash security adjustment (cash will handle portfolio balancing)
          const finalSecurities = await handleCashAdjustment(nextSecurities, pmResult.mkval, Number(updatedSecurity.mkval_current || updatedSecurity.mkval || 0), updatedSecurity.secid)
          
          // Update portfolio market value if not using cash security
          if (!adjustCashToggle) {
            recalculateTotalMarketValue(finalSecurities)
          }
          
          setSecurities(finalSecurities)
        })
        .catch((error) => {
          // Show user notification about the target API failure (save failures are handled separately)
          setNotificationMessage('Transaction unsuccessful. Analysis will continue without updating this security.')
          setTimeout(() => setNotificationMessage(null), 3000)
          
          // Revert to previous values 
          setSecurities((prev) =>
            prev.map((sec) =>
              sec.secid === updatedSecurity.secid
                ? prevRow 
                : sec,
            ),
          )
        })
        .finally(() => {
          endTradeCalculation()
        })

      return
    }

    // Duration What-If edit -> Target duration contribution API only
    if (durChanged) {
      // API Validation - Validate duration parameters
      const formattedDate = dayjs(selectedPortfolio?.date).format('YYYYMMDD')
      if (!formattedDate || isNaN(Number(formattedDate))) {
        setNotificationMessage('Invalid portfolio date. Please select a valid portfolio.')
        return
      }
      const targetDurCont = (updatedSecurity as any).dur_contr_whatif ?? 0
      const durationValidation = validateNumber(targetDurCont)
      if (!durationValidation.isValid) {
        setNotificationMessage(durationValidation.error || 'Invalid target duration contribution. Please enter a valid number.')
        return
      }

      // Calculate portfolio market value for target API call
      // When not using cash security, use current recalculated mkval
      const currentPortfolioMkval = adjustCashToggle 
        ? (effectivePortMkval ?? 0)
        : recalculateTotalMarketValue(securities)

      const durParams = {
        durCont: Number((updatedSecurity as any).dur_contr_whatif ?? 0),
        date: formattedDate,
        units: updatedSecurity.holdings_current || 0,
        mval: Number(updatedSecurity.mval ?? 0),
        fmval: Number(updatedSecurity.fmval ?? updatedSecurity.mval ?? 0),
        durVal: Number(updatedSecurity.edur ?? 0),
        currencyCode: updatedSecurity.currencyCode ?? 0,
        baseCurrencyCode: updatedSecurity.baseCurrencyCode ?? 0,
        portMkval: Number(currentPortfolioMkval),
      }

      startTradeCalculation()

      getTargetDurContResult(durParams)
        .then(async (durResult) => {
          const nextSecurities = securities.map((sec) =>
            sec.secid === updatedSecurity.secid
              ? {
                  ...sec,
                  holdings_whatif: durResult.trades,
                  mkval: durResult.mkval,
                  pmkval_whatif: durResult.pmkval,
                  dur_contr_whatif: durResult.durContrib,
                }
              : sec,
          )

          // Validate market value is a valid number
          if (!validateAndHandleMarketValue(durResult.mkval, nextSecurities)) {
            return
          }

          // Handle cash security adjustment (cash will handle portfolio balancing)
          const finalSecurities = await handleCashAdjustment(nextSecurities, durResult.mkval, Number(updatedSecurity.mkval_current || updatedSecurity.mkval || 0), updatedSecurity.secid)
          
          // Update portfolio market value if not using cash security
          if (!adjustCashToggle) {
            recalculateTotalMarketValue(finalSecurities)
          }
          
          setSecurities(finalSecurities)
        })
        .catch(() => {
          // Show user notification about the failure
          setNotificationMessage('Transaction unsuccessful. Analysis will continue without updating this security.')
          setTimeout(() => setNotificationMessage(null), 3000)
          
          // Revert to previous values 
          setSecurities((prev) =>
            prev.map((sec) =>
              sec.secid === updatedSecurity.secid
                ? prevRow 
                : sec,
            ),
          )
        })
        .finally(() => {
          endTradeCalculation()
        })

      return
    }

    // Quantity What-If edit -> Target Trades API
    if (quantityChanged) {
      const whatIfUnits = updatedSecurity.holdings_whatif || 0

      // API Validation - Validate trade parameters
      const formattedDate = dayjs(selectedPortfolio?.date).format('YYYYMMDD')
      if (!formattedDate || isNaN(Number(formattedDate))) {
        setNotificationMessage('Invalid portfolio date. Please select a valid portfolio.')
        return
      }
      // Validate trades is a number (can be positive or negative)
      const tradesValidation = validateNumber(whatIfUnits)
      if (!tradesValidation.isValid) {
        setNotificationMessage(tradesValidation.error || 'Invalid trade quantity. Please enter a valid number.')
        return
      }

      // Calculate portfolio market value for target API call
      // When not using cash security, use current recalculated mkval
      const currentPortfolioMkval = adjustCashToggle 
        ? (effectivePortMkval ?? 0)
        : recalculateTotalMarketValue(securities)

      const tradeParams = {
        trades: whatIfUnits,
        date: formattedDate,
        units: updatedSecurity.holdings_current || 0, // units = current holdings
        mval: Number(updatedSecurity.mval ?? 0),
        fmval: Number(updatedSecurity.fmval ?? updatedSecurity.mval ?? 0),
        durVal: Number(updatedSecurity.edur ?? 0),
        currencyCode: updatedSecurity.currencyCode ?? 0,
        baseCurrencyCode: updatedSecurity.baseCurrencyCode ?? 0,
        portMkval: Number(currentPortfolioMkval),
      }

      startTradeCalculation()

      getTargetTradesResult(tradeParams)
        .then(async (result) => {
          const nextSecurities = securities.map((sec) =>
            sec.secid === updatedSecurity.secid
              ? {
                  ...sec,
                  holdings_whatif: result.trades,
                  mkval: result.mkval,
                  pmkval_whatif: result.pmkval,
                  dur_contr_whatif: result.durContrib,
                }
              : sec,
          )

          // Validate market value is a valid number
          if (!validateAndHandleMarketValue(result.mkval, nextSecurities)) {
            return
          }

          // Handle cash security adjustment and save portfolio
          const finalSecurities = await handleCashAdjustment(nextSecurities, result.mkval, Number(updatedSecurity.mkval_current || updatedSecurity.mkval || 0), updatedSecurity.secid)
          
          // Update portfolio market value if not using cash security
          if (!adjustCashToggle) {
            recalculateTotalMarketValue(finalSecurities)
          }
          
          setSecurities(finalSecurities)
        })
        .catch((error) => {
          // Show user notification about the target API failure (save failures are handled separately)
          setNotificationMessage('Transaction unsuccessful. Analysis will continue without updating this security.')
          setTimeout(() => setNotificationMessage(null), 3000)
          
          // Revert to previous values 
          setSecurities((prev) =>
            prev.map((sec) =>
              sec.secid === updatedSecurity.secid
                ? prevRow 
                : sec,
            ),
          )
        })
        .finally(() => {
          endTradeCalculation()
        })

      return
    }
  }

  const handleClearSecurities = () => {
    setAvailableSecurities([])
  }

  const handleAddSecurities = (selectedSecurities: any[]) => {
    const formattedSecurities = selectedSecurities.map((sec: any) => ({
      id: sec.id || sec.securityId || `sec-${Date.now()}`,
      securityId: sec.securityId || sec.id,
      description: sec.description,
      coupon: sec.coupon,
      price: sec.price,
      country: sec.country,
      currency: sec.currency,
      rating: sec.rating,
      maturityDate: sec.maturityDate,
    }))

    setAvailableSecurities((prevSecurities) => {
      const existingSecIds = new Set(prevSecurities.map(sec => sec.securityId))
      const uniqueNewSecurities = formattedSecurities.filter(sec => !existingSecIds.has(sec.securityId))
      return [
        ...prevSecurities,
        ...uniqueNewSecurities,
      ]
    })
  }

  const handleTransferToWhatIf = async (selectedSecurities: any[]) => {
    if (selectedSecurities.length === 0) return

    try {
      setAddSecuritiesLoading(true)
      // Get portfolio date for API call
      const portfolioDate = selectedPortfolio?.date
        ? new Date(selectedPortfolio.date)
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, '')
        : new Date().toISOString().slice(0, 10).replace(/-/g, '')

      // Separate securities with and without valid CUSIPs
      const securitiesWithCusip = selectedSecurities.filter((sec) => {
        const cusip = sec.securityId || sec.id
        return cusip && cusip !== 'N/A'
      })

      const securitiesWithoutCusip = selectedSecurities.filter((sec) => {
        const cusip = sec.securityId || sec.id
        return !cusip || cusip === 'N/A'
      }).map((sec) => ({
        secid: sec.securityId || sec.id,
        description: sec.description,
        coup: sec.coupon,
        currency: sec.currency,
        country: sec.country,
        price: sec.price,
        edur: 0,
        qual: sec.rating,
        holdings_current: 0,
        holdings_whatif: 0,
        mkval: 0,
        mkval_current: 0,
        pmkval_current: 0,
        pmkval_whatif: 0,
        sector: '',
        dur_contr_current: 0,
        dur_contr_whatif: 0,
        mval: 0,
        fmval: 0,
        currencyCode: 0,
        baseCurrencyCode: 0,
        matu: null,
      }))

      let enrichedSecurities = [...securitiesWithoutCusip]
      
      // Get existing security IDs for quick lookup
      const existingSecIds = new Set(securities.map(sec => sec.secid));
      // Filter out duplicates before making any API calls
      const newSecuritiesWithCusip = securitiesWithCusip.filter(sec => {
          const cusip = sec.securityId || sec.id;
          return !existingSecIds.has(cusip);
      });
      // Calculate counts
      const totalSecurities = securitiesWithCusip.length;
      const newCount = newSecuritiesWithCusip.length;
      const duplicateCount = totalSecurities - newCount;
      // Show appropriate notification
      if (duplicateCount > 0) {
          if (newCount === 0) {
              // All duplicates
              setNotificationMessage(
                  duplicateCount === 1 
                      ? 'Security already exists in portfolio'
                      : 'All securities already exist in portfolio'
              );
          } else {
              // Some new, some duplicates
              setNotificationMessage(
                  `${newCount} ${newCount === 1 ? 'security' : 'securities'} added, ` +
                  `${duplicateCount} ${duplicateCount === 1 ? 'security' : 'securities'} already exist`
              );
          }
          setTimeout(() => setNotificationMessage(null), 5000);
      }
      // Handle securities with CUSIP using individual API calls
      if (newSecuritiesWithCusip.length > 0) {
        try {
          const enrichedWithDetails = []
          
          for (const sec of newSecuritiesWithCusip) {
            const cusip = sec.securityId || sec.id
            try {
              const securityDetails = await getSecurityDetails(cusip, portfolioDate)
              
              enrichedWithDetails.push({
                secid: cusip,
                axsid: securityDetails.axsid || 0,
                description: securityDetails.description || sec.description,
                coup: securityDetails.coup || sec.coupon,
                currency: securityDetails.currency || sec.currency,
                country: securityDetails.country || sec.country,
                price: Number(securityDetails.price ?? sec.price ?? 0),
                edur: Number(securityDetails.edur ?? 0),
                qual: securityDetails.qual || sec.rating,
                holdings_current: Number(securityDetails.holdings ?? 0),
                holdings_whatif: 0,
                mkval: Number(securityDetails.mkval ?? 0),
                mkval_current: Number(securityDetails.mkval ?? 0),
                pmkval_current: Number(securityDetails.pmkval ?? 0),
                pmkval_whatif: 0,
                sector: securityDetails.sector || '',
                dur_contr_current: Number(securityDetails.dur_contr ?? 0),
                dur_contr_whatif: 0,
                mval: Number(securityDetails.mval ?? 0),
                fmval: Number(
                  securityDetails.fmval ?? securityDetails.mval ?? 0
                ),
                currencyCode: securityDetails.currency_code || 0,
                baseCurrencyCode: securityDetails.currency_code || 0,
                matu: securityDetails.matu ?? null,
                gainloss: Number(securityDetails.gainloss ?? 0),
              })
            } catch (error) {
              // Handle individual security not found
              setNotificationMessage(`Security details not found for CUSIP: ${cusip}. Using basic information.`)
              setTimeout(() => setNotificationMessage(null), 4000)
              enrichedWithDetails.push({
                secid: cusip,
                axsid: 0,
                description: sec.description,
                coup: sec.coupon,
                currency: sec.currency,
                country: sec.country,
                price: sec.price,
                edur: 0,
                qual: sec.rating,
                holdings_current: 0,
                holdings_whatif: 0,
                mkval: 0,
                mkval_current: 0,
                pmkval_current: 0,
                pmkval_whatif: 0,
                sector: '',
                dur_contr_current: 0,
                dur_contr_whatif: 0,
                mval: 0,
                fmval: 0,
                currencyCode: 0,
                baseCurrencyCode: 0,
                matu: null,
                gainloss: 0,
              })
            }
          }

          enrichedSecurities = [...enrichedSecurities, ...enrichedWithDetails]
        } catch (error) {
          console.error('Error fetching batch security details:', error)
          const fallbackSecurities = securitiesWithCusip.map((sec: any) => ({
            secid: sec.securityId || sec.id,
            axsid: 0,
            description: sec.description,
            coup: sec.coupon,
            currency: sec.currency,
            country: sec.country,
            price: sec.price,
            edur: 0,
            qual: sec.rating,
            holdings_current: 0,
            holdings_whatif: 0,
            mkval: 0,
            mkval_current: 0,
            pmkval_current: 0,
            pmkval_whatif: 0,
            sector: '',
            dur_contr_current: 0,
            dur_contr_whatif: 0,
            mval: 0,
            fmval: 0,
            currencyCode: sec.currencyCode || 0,
            baseCurrencyCode: sec.baseCurrencyCode || 0,
            matu: null,
            gainloss: 0,
          }))
          enrichedSecurities = [...enrichedSecurities, ...fallbackSecurities]
        }
      }

      // Add enriched securities to What-If panel, after cash security if it exists
      const newSecurities = enrichedSecurities.filter(newSec => 
        !securities.some(existingSec => existingSec.secid === newSec.secid)
      )
      
      setSecurities((prevSecurities) => {
        const cashIndex = prevSecurities.findIndex(sec => sec.secid === 'USDCASH1')
        
        if (cashIndex !== -1) {
          // Insert after cash security
          const newSecuritiesList = [...prevSecurities]
          newSecuritiesList.splice(cashIndex + 1, 0, ...newSecurities)
          return newSecuritiesList
        } else {
          // No cash security, add to top
          return [...newSecurities, ...prevSecurities]
        }
      })
    } catch (error) {
      console.error('Error transferring securities to What-If panel:', error)
      const formattedSecurities = selectedSecurities.map((sec: any) => ({
        secid: sec.securityId || sec.id,
        price: sec.price,
        edur: 0,
        qual: sec.rating,
        holdings_current: 0,
        holdings_whatif: 0,
        mkval: 0,
        pmkval_current: 0,
        pmkval_whatif: 0,
        sector: '',
        dur_contr_current: 0,
        dur_contr_whatif: 0,
        currencyCode: sec.currencyCode || 0,
        baseCurrencyCode: sec.baseCurrencyCode || 0,
        gainloss: 0,
      }))
      const newFormattedSecurities = formattedSecurities.filter(newSec => 
        !securities.some(existingSec => existingSec.secid === newSec.secid)
      )
      setSecurities((prevSecurities) => {
        const cashIndex = prevSecurities.findIndex(sec => sec.secid === 'USDCASH1')
        
        if (cashIndex !== -1) {
          // Insert after cash security
          const newSecuritiesList = [...prevSecurities]
          newSecuritiesList.splice(cashIndex + 1, 0, ...newFormattedSecurities)
          return newSecuritiesList
        } else {
          // No cash security, add to top
          return [...newFormattedSecurities, ...prevSecurities]
        }
      })
    } finally {
      setAddSecuritiesLoading(false)
    }
  }

  const handleRunWhatIfAnalysis = async () => {
    if (!selectedPortfolio) {
      console.error('Please select a portfolio first')
      return
    }

    // Clear any previous analysis error
    setAnalysisError(null)
    setRunningAnalysis(true)
    try {
      const formattedDate = dayjs(selectedPortfolio.date).format('YYYYMMDD')
      const portDate = parseInt(formattedDate)

      const portfolioName = selectedPortfolio.name

      // Calculate final holdings: current holdings +/- what-if holdings
      const holdings = securities.map((security: any) => {
        const currentHoldings = security.holdings_current || 0
        const whatIfHoldings = security.holdings_whatif || 0
        const finalHoldings = currentHoldings + whatIfHoldings

        return {
          axsid: Number(security.axsid) || 0,
          hld: finalHoldings,
        }
      })

      // First, save the what-if portfolio
      const saveRequest = {
        replaceIfExists: true,
        portDate: portDate,
        portName: portfolioName,
        whatIfPortName: '',
        holdings: holdings,
      }

      await saveWhatIfPortfolio(saveRequest) 

      // Then make API calls to get summary, risk, and sector composition data
      const [summaryResponse, sectorResponse] = await Promise.all([
        getWhatIfSummary(formattedDate, portfolioName),
        getWhatIfSectorComposition(formattedDate, portfolioName),
      ])

      // Transform the summary data
      const transformedSummaryData = transformSummaryData(summaryResponse)

      // For the Transaction Report, recalculate market value for each
      // modified security using the calcmkval API.
      const transactionSecurities = await Promise.all(
        securities.map(async (sec: any) => {
          const whatIfHoldings = sec.holdings_whatif || 0
          if (!whatIfHoldings) {
            return sec
          }

          try {
            // API Validation - Validate calc parameters
            const calcParams = {
              date: formattedDate,
              units: Math.abs(whatIfHoldings),
              mval: Number(sec.mval ?? 0),
              currencyCode: Number(sec.currencyCode ?? 0),
              baseCurrencyCode: Number(
                sec.baseCurrencyCode ?? sec.currencyCode ?? 0,
              ),
            }

            if (!formattedDate || isNaN(Number(formattedDate))) {
              console.warn('Invalid date for market value calculation, skipping security')
              return sec
            }
            if (isNaN(calcParams.units) || calcParams.units <= 0) {
              console.warn('Invalid units for market value calculation, skipping security')
              return sec
            }
            if (isNaN(calcParams.mval) || calcParams.mval < 0) {
              console.warn('Invalid market value for calculation, skipping security')
              return sec
            }

            const calcResult = await calcmkval(calcParams)

            return {
              ...sec,
              mkval: calcResult?.mkval ?? sec.mkval,
            }
          } catch {
            setNotificationMessage('Market value calculation failed for some securities. Using original values.')
            setTimeout(() => setNotificationMessage(null), 4000)
            return {
              ...sec,
              mkval: null as any,
            }
          }
        }),
      )

      // Prepare selected portfolio for navigation, ensuring marketValue is populated
      const navSelectedPortfolio = selectedPortfolio
        ? {
            ...selectedPortfolio,
            marketValue:
              portfolioMkval ?? (selectedPortfolio as any).marketValue ?? null,
          }
        : selectedPortfolio

      // Navigate to dashboard with all datasets
      navigate('/what-if-analysis/dashboard', {
        state: {
          summaryData: transformedSummaryData,
          riskData: summaryResponse.riskData,
          sectorData: sectorResponse.sectorComposition,
          selectedPortfolio: navSelectedPortfolio,
          securities: transactionSecurities,
          portfolios: portfolios,
          summaryApiResponse: summaryResponse,
          riskApiResponse: {
            uuid: summaryResponse.uuid,
            status: summaryResponse.status,
            riskData: summaryResponse.riskData,
          },
          sectorApiResponse: sectorResponse,
          adjustCashToggle: adjustCashToggle,
          portfolioRange: portfolioRange,
        },
      })
    } catch (error) {
      setNotificationMessage('Analysis failed. Please try again or contact support if the problem persists.')
      setTimeout(() => setNotificationMessage(null), 3000)
      setAnalysisError(
        'Unable to complete the analysis because the What-If portfolio could not be saved. Please try again in a few minutes or contact your system administrator if the problem persists.',
      )
    } finally {
      setRunningAnalysis(false)
    }
  }

  return (
    <StaticReportsLayout title={ModuleTypePageTitle.WHAT_IF_REPORTS}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
        }}
      >
        {/* What If Analysis Title */}
        <Typography
          variant='h2'
          sx={{ mb: 3, fontWeight: 'bold', fontSize: '2rem' }}
        >
          What If Analysis
        </Typography>

        {/* Content area with 3-table layout */}
        <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
          {/* Left Column - Portfolio and Add Securities */}
          <Box
            sx={{
              minWidth: '710px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Top: Portfolio Table */}
            <Box sx={{ flex: 1, minHeight: '400px' }}>
              <PortfolioTable
                portfolios={portfolios}
                selectedPortfolio={selectedPortfolio}
                onPortfolioSelect={(portfolio) =>
                  setSelectedPortfolio(portfolio as SelectedPortfolio)
                }
                loading={portfoliosLoading}
                portfolioRange={portfolioRange}
                onPortfolioRangeChange={handlePortfolioRangeChange}
                error={portfoliosError}
              />
            </Box>

            {/* Bottom: Add Securities Table */}
            <Box sx={{ flex: 1, minHeight: '350px' }}>
              <SecurityAccordion
                securities={availableSecurities}
                onAddSecurities={handleAddSecurities}
                onTransferToWhatIf={handleTransferToWhatIf}
                onClearSecurities={handleClearSecurities}
                loading={addSecuritiesLoading}
                selectedPortfolio={
                  selectedPortfolio
                    ? {
                        name: selectedPortfolio.name,
                        date: selectedPortfolio.date,
                        currency: selectedPortfolio.currency,
                        benchmark: selectedPortfolio.benchmarkDescription,
                        benchmarkDescription:
                          selectedPortfolio.benchmarkDescription,
                        totalSecurities: selectedPortfolio.numOfSecurities,
                        marketValue: selectedPortfolio.marketValue,
                      }
                    : undefined
                }
              >
                <AddSecurities
                  securities={availableSecurities}
                  onAddSecurities={handleAddSecurities}
                  onTransferToWhatIf={handleTransferToWhatIf}
                  loading={addSecuritiesLoading}
                />
              </SecurityAccordion>
            </Box>
          </Box>

          {/* Right Column - Security Lists (Portfolio Securities) */}
          <Box sx={{ flex: 1, minWidth: '510px', display: 'flex', flexDirection: 'column' }}>
            <SecurityListsPanel
              selectedPortfolio={selectedPortfolio}
              securities={securities}
              loading={securitiesLoading}
              error={securitiesError}
              onAddSecurity={handleAddSecurity}
              onUpdateSecurity={handleUpdateSecurity}
              onDeleteWarningChange={setDeleteWarning}
              onNotification={(message) => {
                setNotificationMessage(message)
                setTimeout(() => setNotificationMessage(null), 3000)
              }}
              adjustCashToggle={adjustCashToggle}
              onAdjustCashToggleChange={setAdjustCashToggle}
            />
          </Box>
        </Box>

        {/* Bottom Action Bar */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mt: 2,
          }}
        >
          {/* Analysis error message (left side of Run Analysis button) */}
          {analysisError && (
            <Typography
              variant='body2'
              color='error'
              sx={{
                maxWidth: 480,
                textAlign: 'left',
              }}
            >
              {analysisError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {tradeCalculationsLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mr: 2,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant='body2'>
                Updating transaction data...
              </Typography>
            </Box>
          )}
          {deleteWarning && (
            <Typography
              variant='body2'
              color='error'
              sx={{
                mr: 2,
                alignSelf: 'center',
                maxWidth: 400,
                textAlign: 'right',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 205, 210, 0.9)', // light red background
                border: '1px solid #f44336',
              }}
            >
              {deleteWarning}
            </Typography>
          )}
          <Button
            variant='contained'
            color='primary'
            onClick={handleRunWhatIfAnalysis}
            disabled={
              !selectedPortfolio ||
              runningAnalysis ||
              tradeCalculationsLoading ||
              !hasWhatIfChanges()
            }
            sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
          >
            {runningAnalysis ? 'Running Analysis...' : 'Run What-If Analysis'}
          </Button>
          </Box>
        </Box>

        {/* Full-screen-ish overlay while analysis is running */}
        {runningAnalysis && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              pointerEvents: 'all',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant='h4'>Running Analysis...</Typography>
            </Box>
          </Box>
        )}

        {/* Notification Message - Bottom Grid Bar in Yellow */}
        {notificationMessage && (
          <Box sx={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 1.5, 
            backgroundColor: '#fff3cd',
            borderTop: '3px solid #ffc107',
            color: '#000000',
            zIndex: 1000,
            fontSize: '1rem',
            fontWeight: 500,
            textAlign: 'center'
          }}>
            {notificationMessage}
          </Box>
        )}
      </Box>
    </StaticReportsLayout>
  )
}

export default WhatIfReportsPage
